import React, { createContext, useContext, useEffect, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { loginRequest } from '@hooks/auth/auth-config';
import { GetUsuarioSessao, LogoutAPI, TrocarTokenPorJwt } from '@services/Auth/Index';

const AppAuthContext = createContext({
  isAuthenticated: false,
  loading: true,
  role: null,
  email: null,
  nome: null,
  idUnidadeGerencia: null,
  nomeUnidadeGerencia: null,
  idSolicitacao: null,
  loginMode: null,
  logout: () => {},
});

export const useAppAuth = () => {
  const ctx = useContext(AppAuthContext);
  if (!ctx) throw new Error('useAppAuth deve estar dentro de <AppAuthProvider>');
  return ctx;
};

export function AppAuthProvider({ children }) {
  const { accounts, instance, inProgress } = useMsal();

  const [role, setRole] = useState(null);
  const [{ email, nome, idUnidadeGerencia, nomeUnidadeGerencia, idSolicitacao }, setIdentity] =
    useState({
      email: null,
      nome: null,
      idUnidadeGerencia: null,
      nomeUnidadeGerencia: null,
      idSolicitacao: null,
    });

  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loginMode, setLoginMode] = useState(null);

  const clearSession = () => {
    sessionStorage.clear();
    setRole(null);
    setIdentity({
      email: null,
      nome: null,
      idUnidadeGerencia: null,
      nomeUnidadeGerencia: null,
      idSolicitacao: null,
    });
    setLoginMode(null);
    setIsAuthenticated(false);
  };

  const loadUserSession = async () => {
    try {
      const data = await GetUsuarioSessao();

      setRole(data.perfil);
      setIdentity({
        email: data.email,
        nome: data.nome,
        idUnidadeGerencia: data.idUnidadeGerencia,
        nomeUnidadeGerencia: data.nomeUnidadeGerencia,
        idSolicitacao: data.idSolicitacao,
      });
      setLoginMode(data.loginMode ?? null);
      setIsAuthenticated(true);
    } catch {
      clearSession();
    } finally {
      setLoading(false);
    }
  };

  const trySsoSilent = async () => {
    try {
      if (accounts.length === 0) return;
      const { accessToken } = await instance.acquireTokenSilent({
        ...loginRequest,
        account: accounts[0],
      });

      // cria sessão na API
      await TrocarTokenPorJwt(accessToken, 'sso');

      // só depois carrega dados do usuário
      await loadUserSession();
    } catch {
      clearSession();
      setLoading(false);
    }
  };

  useEffect(() => {
    if (inProgress === InteractionStatus.None) {
      const hasAccount = accounts.length > 0;

      if (hasAccount && !email) {
        // só chama GetUsuarioSessao se cookie já existir
        GetUsuarioSessao()
          .then((data) => {
            setRole(data.perfil);
            setIdentity({
              email: data.email,
              nome: data.nome,
              idUnidadeGerencia: data.idUnidadeGerencia,
              nomeUnidadeGerencia: data.nomeUnidadeGerencia,
              idSolicitacao: data.idSolicitacao,
            });
            setLoginMode(data.loginMode ?? null);
            setIsAuthenticated(true);
            setLoading(false);
          })
          .catch(() => {
            // se não tem cookie ainda, tenta SSO
            trySsoSilent();
          });
      } else if (!hasAccount) {
        setLoading(false);
      } else {
        setLoading(false);
      }
    }
  }, [accounts, inProgress]);

  const logout = async () => {
    await LogoutAPI();
    clearSession();
    instance.setActiveAccount(null);
    await instance.logoutRedirect({ postLogoutRedirectUri: '/Login' });
  };

  return (
    <AppAuthContext.Provider
      value={{
        isAuthenticated,
        loading,
        role,
        email,
        nome,
        idUnidadeGerencia,
        nomeUnidadeGerencia,
        idSolicitacao,
        loginMode,
        logout,
      }}
    >
      {children}
    </AppAuthContext.Provider>
  );
}
